import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Redactor from '../../../static/redactor';

/*
Redactor for the radio `text` and `subtitle` fields: the editor pspanel used for the
same fields, so legacy HTML goes through the same hands it was written with. (The
manager's Tiptap editor is built around email markup and drops what it does not model,
images and iframes included, which is the wrong trade for 7,000 existing texts.)

Two variants:
  - full:   text. Formatting, bold, italic, strike, lists, links and the HTML source.
            No image/file buttons: they need an upload endpoint Redactor does not have
            here; an image can still go in through the HTML view.
  - inline: subtitle. Bold and italic only, line breaks instead of paragraphs, and
            pasting keeps nothing else.

UNTOUCHED MEANS UNCHANGED. The form sends only the fields the editor changed (see
utils/radioShowForm.js), and Redactor may tidy legacy markup the moment it loads it. So
the stored value is reported back verbatim until the content really differs from what
Redactor first showed: a tidy-up alone never marks the field as changed.

FLUSH BEFORE SAVE. Redactor copies the editor into its textarea 200 ms after the last
keystroke, so a Save clicked within that window would read the previous text. The form
registers each editor through `registerFlush`; flush() syncs right now (Redactor's
`hardsync`, or, in the HTML view, the textarea that view edits directly), reports the
value through onChange and returns it, so the form can build the mutation from it.

Uncontrolled: the value is read once, at mount. The form mounts after the show has
loaded and keeps every language mounted (hidden), so that is all it needs. static/redactor
exports nothing on the server; the effect only runs in the browser.
*/

const OPTIONS = {
  full: {
    buttons: ['html', 'format', 'bold', 'italic', 'deleted', 'lists', 'link'],
  },
  inline: {
    buttons: ['bold', 'italic'],
    breakline: true,
    pasteBlockTags: [],
    pasteInlineTags: ['br', 'strong', 'b', 'em', 'i'],
  },
};

const RadioRichText = ({
  id, value, onChange, variant, registerFlush,
}) => {
  const textareaRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const initialValueRef = useRef(value);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element || typeof Redactor !== 'function') return undefined;

    let initialCode = null;
    const reported = html => (html === initialCode ? initialValueRef.current : html);
    const report = (html) => {
      if (initialCode === null || typeof html !== 'string') return;
      onChangeRef.current(reported(html));
    };

    let instance = null;
    try {
      instance = Redactor(element, {
        ...OPTIONS[variant],
        styles: true,
        callbacks: {
          changed: report,
          // `changed` misses edits made in the HTML view until it is closed; blur catches them.
          blur: () => {
            if (instance && instance.source) report(instance.source.getCode());
          },
        },
      });
      initialCode = instance && instance.source ? instance.source.getCode() : null;
    } catch (error) {
      console.log('Radio: Redactor could not start', error);
    }

    // Returns the editor's current value (undefined if Redactor never started).
    const flush = () => {
      if (!instance || !instance.source || initialCode === null) return undefined;
      const inHtmlView = element.classList.contains('redactor-source-open');
      if (!inHtmlView) instance.broadcast('hardsync');
      const html = inHtmlView ? element.value : instance.source.getCode();
      if (typeof html !== 'string') return undefined;
      report(html);
      return reported(html);
    };
    if (registerFlush) registerFlush(flush);

    return () => {
      if (registerFlush) registerFlush(null);
      try {
        Redactor(element, 'destroy');
      } catch (error) {
        console.log('Radio: Redactor cleanup', error);
      }
    };
  }, []);

  return (
    <div className={variant === 'inline' ? 'radio-inline-editor' : ''}>
      <textarea ref={textareaRef} id={id} defaultValue={value || ''} />
    </div>
  );
};

RadioRichText.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['full', 'inline']),
  // Called with a flush function at mount and with null at unmount.
  registerFlush: PropTypes.func,
};

RadioRichText.defaultProps = {
  value: '',
  variant: 'full',
  registerFlush: null,
};

export default RadioRichText;
